import { UserInvite } from "@contentchef/contentchef-types";
import { notification } from "antd";
import { fetchAuthSession } from "aws-amplify/auth";
import { inject, observer } from "mobx-react";
import React from "react";
import { FormattedMessage } from "react-intl";
import { RouteComponentProps } from "react-router";
import { Loader } from "../../components";
import { ROOT_ROUTE } from "../../constants/routing-constants";
import withInvites from "../../hoc/withInvites";
import { UserPermissionStore, UsersStore } from "../../stores";
import { ApiStoreModel } from "../../stores/apiStore/apiStore";
import { InviteStoreModel } from "../../stores/inviteStore";
import { OnboardingStoreModel } from "../../stores/onboardingStore";
import Invite from "./Invite";
import InviteAlreadyAccepted from "./InviteAlreadyAccepted";
import InviteNotFound from "./InviteNotFound";
import UserAlreadyInSpace from "./UserAlreadyInSpace";
import classes from "./index.module.scss";

export interface AcceptInviteSceneProps extends RouteComponentProps {}

interface DecoratedAcceptInviteSceneProps extends AcceptInviteSceneProps {
  apiStore: ApiStoreModel;
  inviteStore: InviteStoreModel;
  onboardingStore: OnboardingStoreModel;
  userPermissionsStore: UserPermissionStore;
  usersStore: UsersStore;
}

interface AcceptInviteSceneState {
  didLoad: boolean;
  isAccepting: boolean;
  isUserAlreadyInSpace: boolean;
}

function getInitialState(): AcceptInviteSceneState {
  return {
    didLoad: false,
    isAccepting: false,
    isUserAlreadyInSpace: false,
  };
}

function getQuerystring(location: Location) {
  const urlparams = new URLSearchParams(location.search);
  const guid = urlparams.get("invite");
  const spaceId = urlparams.get("spaceId");
  const email = urlparams.get("email");

  return {
    guid,
    spaceId,
    email,
  };
}

function isInvite(arg: unknown): arg is UserInvite {
  return typeof arg === "object" && arg !== null && "guid" in arg && "spaceId" in arg;
}

function isString(arg: unknown): arg is string {
  return typeof arg === "string";
}

@inject("apiStore", "inviteStore", "onboardingStore", "userPermissionsStore", "usersStore")
@observer
class AcceptInviteScene extends React.Component<AcceptInviteSceneProps, AcceptInviteSceneState> {
  public get decoratedProps(): DecoratedAcceptInviteSceneProps {
    return this.props as DecoratedAcceptInviteSceneProps;
  }

  public state = getInitialState();

  public async componentDidMount(): Promise<void> {
    const { guid, spaceId } = getQuerystring(this.props.location as any);

    if (!isString(guid) || !isString(spaceId)) {
      return;
    }

    this.decoratedProps.apiStore.changeCurrentSpace(spaceId);

    const inviteStore = this.decoratedProps.inviteStore;

    await inviteStore.get(guid);

    this.setState({ didLoad: true });
  }

  public handleAccept = async () => {
    const { apiStore, inviteStore, onboardingStore, userPermissionsStore } = this.decoratedProps;
    const { guid, spaceId, email } = getQuerystring(this.props.location as any);

    this.setState({ isAccepting: true });

    try {
      await inviteStore.acceptInvite({ guid: guid!, email: email! });
      const initialPermissionsCount = userPermissionsStore.permissionsList.length;
      const timeout = 2500;
      const poll = async (pollCount: number) => {
        await userPermissionsStore.list();
        const permissionsCount = userPermissionsStore.permissionsList.length;

        if (pollCount > 10) {
          throw new Error("");
        }

        if (permissionsCount <= initialPermissionsCount) {
          setTimeout(poll, timeout, pollCount + 1);
          return;
        }

        notification.success({
          message: (
            <FormattedMessage
              defaultMessage="Access granted to space {spaceId}"
              id="scenes.AcceptInvite.acceptSuccess"
              values={{ spaceId }}
            />
          ),
        });

        // Force refresh session to pick up updated permissions
        await fetchAuthSession({ forceRefresh: true });

        await onboardingStore.postAcceptInvite();
        await userPermissionsStore.list();
        apiStore.switchToSpace(spaceId!);
      };

      setTimeout(poll, timeout, 0);
    } catch (error: any) {
      notification.error({
        message: (
          <FormattedMessage
            defaultMessage="{error}"
            id="scenes.AcceptInvite.acceptError"
            values={{ error: error.message }}
          />
        ),
      });
      this.setState({
        isAccepting: false,
        isUserAlreadyInSpace: error.type === "UserAlreadyInSpaceError",
      });
    }
  };

  public handleHomeRedirect = () => this.props.history.push(ROOT_ROUTE);

  /** Which account accepts, and whether the invite was sent to another one: the usual way to accept with the wrong one. */
  public renderSignedInAs(inviteEmail: string | null) {
    const email = this.decoratedProps.usersStore.currentUser?.userDetails.email;
    if (!email) {
      return null;
    }
    const sentTo = inviteEmail ?? getQuerystring(this.props.location as any).email;
    const otherAccount = !!sentTo && sentTo.toLowerCase() !== email.toLowerCase();
    return (
      <p className={otherAccount ? `${classes.SignedInAs} ${classes.SignedInAsOther}` : classes.SignedInAs}>
        {otherAccount ? (
          <FormattedMessage
            defaultMessage="Signed in as {email}: this invite was sent to {sentTo}."
            id="scenes.AcceptInvite.signedInAsOther"
            values={{ email: <strong>{email}</strong>, sentTo: <strong>{sentTo}</strong> }}
          />
        ) : (
          <FormattedMessage
            defaultMessage="Signed in as {email}"
            id="scenes.AcceptInvite.signedInAs"
            values={{ email: <strong>{email}</strong> }}
          />
        )}
      </p>
    );
  }

  public render() {
    const { isUserAlreadyInSpace } = this.state;
    const invite = this.decoratedProps.inviteStore.currentInvite;

    if (!this.state.didLoad) {
      return <Loader />;
    }

    return (
      <div>
        {this.renderSignedInAs(isInvite(invite) ? invite.userEmail : null)}
        {!isInvite(invite) && <InviteNotFound onClick={this.handleHomeRedirect} />}
        {isInvite(invite) && invite.accepted && !isUserAlreadyInSpace && (
          <InviteAlreadyAccepted onClick={this.handleHomeRedirect} />
        )}
        {isInvite(invite) && !invite.accepted && !isUserAlreadyInSpace && (
          <Invite invite={invite} isAccepting={this.state.isAccepting} onClick={this.handleAccept} />
        )}
        {isUserAlreadyInSpace && <UserAlreadyInSpace onClick={this.handleHomeRedirect} />}
      </div>
    );
  }
}

export default withInvites(AcceptInviteScene);
